import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Button, Flex, Input, Text, InputGroup, InputLeftElement, InputRightElement, Image, Spinner, 
  Modal, ModalContent, ModalOverlay, 
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { VerificationRecordTable, VerificationRecordItem } from './HistoryTable';

import visibleIcon from '../../Resources/Images/Icons/visible_icon.svg';
import invisibleIcon from '../../Resources/Images/Icons/invisible_icon.svg';


// Mock data for verification records
const records: VerificationRecordItem[] = [
  {
    id: 1,
    verifyTime: '2026-05-06 14:30',
    result: '成功',
    certificateNo: 'CERT-2026-0001',
    studentName: '王小明',
    certNo: 'A123456789',
    verifyModule: '國文系',
  },
  {
    id: 2,
    verifyTime: '2026-05-04 14:30',
    result: '失敗',
    certificateNo: 'CERT-2026-0002',
    studentName: '王大明',
    certNo: 'A246891357',
    verifyModule: '數學系',
  }
];

interface LogInSectionProps {
  status: number;
  setStatus: React.Dispatch<React.SetStateAction<number>>;
}

type LoginFormPanelProps = {
  onLoginSuccess: () => void;
};

const LoginFormPanel: React.FC<LoginFormPanelProps> = ({ onLoginSuccess }) => {
  const { t } = useTranslation();

  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handlePasswordClick = () => setShowPassword(!showPassword);

  const handleLogin = () => {
    setLoading(true);

    setTimeout(() => {
      onLoginSuccess();
    }, 300);
  };

  return (
    <Flex
      w="100%"
      maxW="1040px"
      minH="440px"
      bg="#FFF9D0"
      borderRadius="10px"
      direction="column"
      align="center"
      justify="center"
      py="50px"
      gap="60px"
    >
      {/* Panel Subtitle */}
      <Text fontFamily="'Open Sans'" fontWeight={600} fontSize="20px" lineHeight="120%" color="#000000">
        {t('verification.panelTitle')}
      </Text>

      {/* Inputs & Buttons */}
      <Flex direction="column" align="center" gap="30px" w="100%">
        <Flex direction="column" gap="15px" w="100%" maxW="300px">
          {/* Tax ID Input */}
          <InputGroup h="45px">
            <InputLeftElement h="45px" w="60px">
              <Text fontSize="14px" fontWeight={600} color="#0A0A25">
                {t('verification.taxId')}
              </Text>
            </InputLeftElement>
            <Input
              h="45px"
              bg="#FFFFFF"
              border="1px solid #A8B8C2"
              borderRadius="3px"
              pl="60px"
              fontSize="14px"
              color="#4F6470"
              _focus={{ borderColor: '#A8B8C2', boxShadow: 'none' }}
            />
          </InputGroup>

          {/* Password Input */}
          <InputGroup h="44px">
            <InputLeftElement h="44px" w="60px">
              <Text fontSize="14px" fontWeight={600} color="#0A0A25">
                {t('verification.password')}
              </Text>
            </InputLeftElement>
            <Input
              type={showPassword ? 'text' : 'password'}
              h="44px"
              bg="#FFFFFF"
              border="1px solid #A8B8C2"
              borderRadius="3px"
              pl="60px"
              fontSize="14px"
              _focus={{ borderColor: '#A8B8C2', boxShadow: 'none' }}
            />
            <InputRightElement h="44px" cursor="pointer" onClick={handlePasswordClick}>
              <Image src={showPassword ? visibleIcon : invisibleIcon} alt="password toggle" boxSize="24px" />
            </InputRightElement>
          </InputGroup>
        </Flex>

        {/* Button Area */}
        <Flex align="center" gap="20px" wrap="wrap" justify="center">
          <Button
            as={Link}
            to="/register"
            variant="outline"
            h="37px"
            px="12px"
            borderColor="#00AAFF"
            color="#00AAFF"
            fontSize="14px"
            fontWeight={400}
            borderRadius="4px"
            _hover={{ bg: 'blue.50' }}
          >
            {t('verification.register')}
          </Button>

          <Button
            variant="outline"
            h="37px"
            px="12px"
            borderColor="#D21C1B"
            color="#D21C1B"
            fontSize="14px"
            fontWeight={400}
            borderRadius="4px"
            _hover={{ bg: 'red.50' }}
            onClick={() => setIsForgotPasswordModalOpen(true)}
          >
            {t('verification.forgotPassword')}
          </Button>

          <Button
            h="37px"
            px="12px"
            bg="#E6F7FF"
            color="#FFFFFF"
            fontSize="14px"
            fontWeight={400}
            borderRadius="4px"
            _hover={{ bg: '#E6F7FF' }}
            cursor="not-allowed"

            // mock UI action
            onClick={handleLogin}
            isDisabled={loading}
          >
            {t('verification.login')}
          </Button>
        </Flex>
      </Flex>

      {/* Loading mask */}
      {loading && (
        <Flex position="fixed" inset={0} bg="rgba(0,0,0,0.35)" zIndex={9998} align="center" justify="center">
          <Spinner w="60px" h="60px" thickness="5px" speed="0.75s" color="#00AAFF" emptyColor="rgba(255,255,255,0.3)" />
        </Flex>
      )}

      {/* Forgot Password Modal */}
      <Modal isOpen={isForgotPasswordModalOpen} onClose={() => setIsForgotPasswordModalOpen(false)} isCentered>
        <ModalOverlay bg="rgba(37, 42, 49, 0.12)" />
        <ModalContent
          w="540px"
          maxW="540px"
          bg="#FFFFFF"
          boxShadow="0px 12px 24px rgba(37, 42, 49, 0.24), 0px 8px 60px rgba(37, 42, 49, 0.32)"
          borderRadius="12px"
          p="24px"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap="16px"
        >
          <Text
            fontFamily="Open Sans"
            fontWeight={600}
            fontSize="20px"
            lineHeight="120%"
            letterSpacing="0.01em"
            color="#000000"
          >
            {t('verification.forgotPasswordModalTitle')}
          </Text>

          <Text
            fontFamily="Open Sans"
            fontWeight={400}
            fontSize="16px"
            lineHeight="150%"
            letterSpacing="0.01em"
            color="#0A0A25"
            whiteSpace="pre-line"
          >
            {t('verification.forgotPasswordModalBody')}
          </Text>

          <Flex justify="flex-end" align="center" gap="8px" w="100%">
            <Button
              type="button"
              h="37px"
              px="12px"
              bg="#00AAFF"
              borderRadius="4px"
              color="#FFFFFF"
              fontFamily="Open Sans"
              fontSize="14px"
              fontWeight={400}
              lineHeight="150%"
              letterSpacing="0.01em"
              onClick={() => setIsForgotPasswordModalOpen(false)}
              _hover={{ bg: '#0099EE' }}
              _active={{ bg: '#0088DD' }}
            >
              {t('verification.close')}
            </Button>
          </Flex>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

type AfterLoginPanelProps = {
  onLogout: () => void;
};

const QrCodePanel = () => {
  const { t } = useTranslation();

  return (
    <Flex
      w="100%"
      maxW="1040px"
      minH="440px"
      bg="#FFF9D0"
      borderRadius="10px"
      direction="column"
      align="center"
      justify="center"
      py="50px"
      gap="60px"
    >
      <Text fontSize="16px" color="#0A0A25">{t('verification.qrPanelPlaceholder')}</Text>
    </Flex>
  )
};

const VerifyHistoryPanel = () => {
  const { t } = useTranslation();

  return (
    <Flex w="100%" align="center" justify="center" minH="260px" direction="column">
      <Text
        fontFamily="'Open Sans'"
        fontWeight={600}
        fontSize="25px"
        lineHeight="133%"
        textAlign="center"
        color="#000000"
        mb="10px"
      >
        {t('verification.historyPanelTitle')}
      </Text>
      <VerificationRecordTable records={records} />
    </Flex>
  )
};


const BuildVpPanel = () => {
  const { t } = useTranslation();

  return (
    <Flex w="100%" align="center" justify="center" minH="260px">
      <Text fontSize="16px" color="#0A0A25">{t('verification.vpPanelPlaceholder')}</Text>
    </Flex>
  )
};

type AfterLoginView = 'qr' | 'history' | 'vp';

const AfterLoginPanel: React.FC<AfterLoginPanelProps> = ({ onLogout }) => {
  const { t } = useTranslation();

  const [activeView, setActiveView] = useState<AfterLoginView>('history');

  return (
    <>
      <Flex
        w="100%"
        maxW="1040px"
        bg="#FFF9D0"
        borderRadius="10px"
        direction="column"
        align="center"
        justify="center"
        py="20px"
        gap="24px"
      >
          <Text
            fontFamily="'Open Sans'"
            fontWeight={600}
            fontSize="20px"
            lineHeight="120%"
            textAlign="center"
            color="#000000"
          >
            {t('verification.unitLabel')} |
          </Text>
        {/* Button Area */}
        <Flex align="center" gap="20px" wrap="wrap" justify="center">
          <Button
            variant="accentYellow"
            h="37px"
            px="12px"
            fontSize="14px"
            fontWeight={400}
            borderRadius="4px"
            onClick={() => setActiveView('qr')}
          >
            {t('verification.qrPanelTitle')}
          </Button>
          <Button
            variant="accentYellow"
            h="37px"
            px="12px"
            fontSize="14px"
            fontWeight={400}
            borderRadius="4px"
            onClick={() => setActiveView('history')}
          >
            {t('verification.historyPanelTitle')}
          </Button>
          <Button
            as={Link}
            to="/vc-verification/edit"
            variant="actionPrimary"
            h="37px"
            px="12px"
            fontSize="14px"
            fontWeight={400}
            borderRadius="4px"
          >
            {t('verification.unitDataPanelTitle')}
          </Button>
          <Button
            variant="outline"
            h="37px"
            px="12px"
            borderColor="#00AAFF"
            color="#00AAFF"
            fontSize="14px"
            fontWeight={400}
            borderRadius="4px"
            _hover={{ bg: 'blue.50' }}
            onClick={() => setActiveView('vp')}
          >
            {t('verification.vpPanelTitle')}
          </Button>
          <Button
            variant="neutral"
            h="37px"
            px="12px"
            borderColor="#000000"
            color="#000000"
            fontSize="14px"
            fontWeight={400}
            borderRadius="4px"
            onClick={onLogout}
          >
            {t('verification.logout')}
          </Button>
        </Flex>
      </Flex>
      <Flex py="32px" align="stretch" justify="center" w="100%" maxW="1040px">
        {activeView === 'qr' && <QrCodePanel />}
        {activeView === 'history' && <VerifyHistoryPanel />}
        {activeView === 'vp' && <BuildVpPanel />}
      </Flex>
    </>
  );
};


const LogInSection: React.FC<LogInSectionProps> = ({ status, setStatus }) => {
  const { t } = useTranslation();


  return (
    <Flex w="100%" direction="column" align="center">
      {status === 0 && (
        <LoginFormPanel onLoginSuccess={() => setStatus(1)} />
      )}

      {status === 1 && (
        <AfterLoginPanel onLogout={() => setStatus(0)} />
      )}
    </Flex>
  );
  
};

export default LogInSection;
